// A game's page in the browser (/apps/:id): drawn as game.ts writes it, then
// its chart (chart-draw.ts) and range's stats once the series loads, and its
// toggles, range tabs and the play-time insight wired up.

import { type AppResponse, type HistoryRange, type HistoryResponse, isHistoryRange } from "../../../shared/api.ts";
import { playTimes } from "../../../shared/evenings.ts";
import { appPath } from "../../../shared/site.ts";
import { errorMessage, isNotFound } from "../api-client.ts";
import { HOURLY_RANGES, playHint, stats, type StatText } from "../chart/chart-panel.ts";
import { chartOf, chartStatus, renderChart } from "../chart/chart-draw.ts";
import { type Point, dayLabel, dropPartialDay, high, low, weekOf } from "../chart/chart.ts";
import { app, element, eventTarget } from "../dom.ts";
import { setMeta } from "../head.ts";
import { displayName } from "../markup.ts";
import { appLoadingMeta, appMeta, appNotFoundMeta, appNotFoundPage } from "../meta.ts";
import type { RememberedSet } from "../prefs.ts";
import { listBackLink } from "../router.ts";
import { compareWith, pageRange } from "../routes.ts";
import { api, isCurrent, observeResize } from "../state.ts";
import {
  isShaded,
  listedKinds,
  pageEvenings,
  previewEvenings,
  previewKind,
  setPreviewEvenings,
  setPreviewKind,
  shownEvenings,
  shownKinds,
  shownSales
} from "../toggles.ts";
import { number } from "../figures.ts";
import { gamePage } from "./game.ts";
import { updateRows } from "../updates.ts";

// When the range's high or low was, on the hourly ranges.
const hourLabel = new Intl.DateTimeFormat(undefined, {
  month: "short",
  day: "numeric",
  hour: "numeric"
});

export async function renderApp(token: number, appId: number): Promise<void> {
  // Previews belong to the page they were started on; a navigation can skip
  // the pointerleave or blur that would end one. So does what the play-time
  // insight shaded.
  pageEvenings.clear();
  setPreviewEvenings([]);
  setPreviewKind(null);

  const range = pageRange(location.search);
  const back = listBackLink();

  setMeta(appLoadingMeta(appId));
  app.innerHTML = `${back}<div class="panel status">Loading…</div>`;

  let game: AppResponse;

  try {
    game = await api<AppResponse>(`/api/apps/${appId}`);
  } catch (err) {
    if (!isCurrent(token)) return;

    if (isNotFound(err)) {
      setMeta(appNotFoundMeta(appId));
      app.innerHTML = appNotFoundPage(appId);
    } else {
      app.innerHTML = `${back}<div class="panel status">${errorMessage(err)}</div>`;
    }
    return;
  }

  if (!isCurrent(token)) return;

  const name = displayName(game);
  setMeta(appMeta(game, name));

  app.innerHTML = gamePage(game, name, {
    back,
    range,
    shownKinds,
    showSales: shownSales.has("sales"),
    listedKinds,
    isShaded
  });

  const chart = element(".chart", HTMLElement, app);
  // Its evenings redraw as nothing until there's a chart to shade.
  const drawn = chartOf(chart);

  observeResize(chart, () => {
    if (drawn.series && chart.clientWidth !== drawn.renderedWidth) {
      renderChart(chart, drawn.series);
    }
  });

  // The chart's toggles re-render it from the series already loaded rather
  // than refetching. A kind's toggle just clicked previews nothing.
  const redraw = (): void => {
    if (drawn.series) renderChart(chart, drawn.series);
  };
  wireToggles(
    element(".update-toggles", HTMLElement, app),
    "button[data-update-kind]",
    (b) => b.dataset.updateKind,
    shownKinds,
    () => {
      setPreviewKind(null);
      redraw();
    }
  );
  wireToggles(element(".sale-toggles", HTMLElement, app), "button[data-sales]", () => "sales", shownSales, redraw);

  // The list's own toggles pick what it lists, leaving the chart's markers be.
  wireToggles(app.querySelector(".list-toggles"), "button[data-list-kind]", (b) => b.dataset.listKind, listedKinds, () => {
    element(".update-rows", HTMLElement, app).innerHTML = updateRows(game, listedKinds);
  });

  // Shows each evening toggle, and the play-time insight, as pressed while
  // its evenings are shaded, by either of them.
  function syncEveningControls(): void {
    for (const button of app.querySelectorAll<HTMLElement>("[data-evening]")) {
      button.setAttribute("aria-pressed", String(isShaded(button.dataset.evening ?? "")));
    }
    syncCard();
  }

  // A toggle turns its region on to remember, or off whichever turned it on.
  // Only the shading changes, so it's redrawn without the rest of the chart.
  element(".evening-toggles", HTMLElement, app).addEventListener("click", (event) => {
    const evening = eventTarget(event, "button[data-evening]")?.dataset.evening;
    if (evening === undefined) return;

    const on = !isShaded(evening);
    setPreviewEvenings([]);
    pageEvenings.delete(evening);
    if (on) shownEvenings.add(evening);
    else shownEvenings.delete(evening);
    shownEvenings.save();
    syncEveningControls();
    drawn.drawEvenings();
  });

  // Previews `regions`' evenings (none for []), redrawing only on a change.
  const previewRegions = (regions: string[]): void => {
    if (regions.join() === previewEvenings.join()) return;
    setPreviewEvenings(regions);
    drawn.drawEvenings();
  };

  // Hovering a toggle that's off previews turning it on: its kind's update
  // markers, faded, or its region's evenings, until the pointer leaves. A
  // click ends the preview (above), so a toggle just turned off doesn't look
  // as if it's still on while the pointer rests on it.
  const previewToggle = (button: HTMLElement, on: boolean): void => {
    const { updateKind, evening } = button.dataset;

    if (updateKind) {
      setPreviewKind(on ? updateKind : null);
      showPreviewMarks(chart);
    } else {
      previewRegions(on && evening !== undefined ? [evening] : []);
    }
  };

  for (const button of app.querySelectorAll<HTMLElement>("[data-update-kind], [data-evening]")) {
    button.addEventListener("pointerenter", () => {
      if (button.getAttribute("aria-pressed") === "false") previewToggle(button, true);
    });
    button.addEventListener("pointerleave", () => previewToggle(button, false));
  }

  // The play-time insight (chart-panel.ts's playTimeCard) toggles its evenings like
  // the evening toggles, all of them at once, but for this page only: a click
  // shades them (pageEvenings, cleared by the next navigation) and another
  // clears them, remembered toggles included. It's pressed while every one is
  // shaded, so the toggles under the chart can turn it off too. Hovering it
  // while it's off previews them, as hovering an off toggle does.
  const card = app.querySelector<HTMLElement>(PLAY_TIME_TOGGLE);
  const cardEvenings: string[] = card ? (playTimes(game.tags).evenings ?? []) : [];

  function syncCard(): void {
    card?.setAttribute("aria-pressed", String(cardEvenings.every(isShaded)));
  }

  function toggleCard(): void {
    if (!card || card.getAttribute("aria-disabled") === "true") return;

    const on = card.getAttribute("aria-pressed") !== "true";

    for (const region of cardEvenings) {
      if (on) {
        if (!shownEvenings.has(region)) pageEvenings.add(region);
      } else {
        pageEvenings.delete(region);
        shownEvenings.delete(region);
      }
    }
    if (!on) shownEvenings.save();
    setPreviewEvenings([]);
    syncEveningControls();
    drawn.drawEvenings();
  }

  if (card) {
    syncCard();
    card.addEventListener("click", toggleCard);
    // A <div> button, so Enter and Space are wired up by hand.
    card.addEventListener("keydown", (event) => {
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        toggleCard();
      }
    });
    card.addEventListener("pointerenter", () => {
      if (
        card.getAttribute("aria-pressed") === "false" &&
        card.getAttribute("aria-disabled") !== "true"
      ) {
        previewRegions(cardEvenings);
      }
    });
    card.addEventListener("pointerleave", () => previewRegions([]));
  }

  element(".range-tabs", HTMLElement, app).addEventListener("click", (event) => {
    const clicked = eventTarget(event, "button[data-range]")?.dataset.range ?? null;
    if (!isHistoryRange(clicked)) return;

    history.replaceState(history.state, "", `${appPath(appId)}?range=${clicked}`);
    // Compare opens on the range shown here (gamePage wrote the first).
    app.querySelector(".compare-link")?.setAttribute("href", compareWith(game, clicked));
    showRange(token, game, clicked);
  });

  showRange(token, game, range);
}

// The play-time insight, when it's a toggle for evenings (chart-panel.ts's
// playTimeCard).
const PLAY_TIME_TOGGLE = ".insight.play-time[role=button]";

// The evening toggles, and the play-time insight's toggle, only work where
// there are hours to shade: hidden and disabled on the daily and weekly
// ranges, any preview ended, and the insight's hint saying where it works.
function showEveningControls(hourly: boolean): void {
  element(".evening-toggles", HTMLElement, app).hidden = !hourly;

  if (!hourly) setPreviewEvenings([]);

  const card = app.querySelector(PLAY_TIME_TOGGLE);
  if (!card) return;

  card.setAttribute("aria-disabled", String(!hourly));
  element(".play-hint", HTMLElement, card).textContent = playHint(hourly);
}

// Shows the faded markers of the kind whose off toggle is hovered
// (toggles.ts's previewKind), and hides the rest; see chart-draw.ts's
// renderChart.
function showPreviewMarks(el: Element): void {
  for (const layer of el.querySelectorAll<SVGGElement>(".preview-marks")) {
    if (layer.dataset.kind === previewKind) layer.removeAttribute("hidden");
    else layer.setAttribute("hidden", "");
  }
}

// Bumped on every range asked for, so a range that loads after a later
// one was picked draws nothing.
let rangeRequest = 0;

async function showRange(token: number, game: AppResponse, range: HistoryRange): Promise<void> {
  const request = ++rangeRequest;
  const chart = element(".chart", HTMLElement, app);

  for (const button of app.querySelectorAll<HTMLElement>("[data-range]")) {
    button.setAttribute("aria-pressed", String(button.dataset.range === range));
  }
  showEveningControls(HOURLY_RANGES.has(range));

  chart.classList.add("loading");

  try {
    const series = dropPartialDay(
      await api<HistoryResponse>(`/api/apps/${game.app_id}/history?range=${range}`)
    );
    if (!isCurrent(token) || request !== rangeRequest) return;

    renderStats(element(".stats", HTMLElement, app), game, series);
    renderChart(chart, series);
  } catch (err) {
    if (!isCurrent(token) || request !== rangeRequest) return;

    chartStatus(chart, `<div class="status">${errorMessage(err)}</div>`);
  } finally {
    if (request === rangeRequest) chart.classList.remove("loading");
  }
}

function renderStats(el: Element, game: AppResponse, series: HistoryResponse): void {
  const { resolution, range } = series;
  const points: readonly Point[] = series.points;
  const when = (t: number): string =>
    resolution === "hour"
      ? hourLabel.format(t * 1000)
      : `${weekOf(resolution)}${dayLabel.format(t * 1000)}`;

  let rangeStats: { high: StatText; low: StatText } | null = null;
  if (points.length) {
    const peak = points.reduce((a, p) => (high(p) > high(a) ? p : a));
    const trough = points.reduce((a, p) => (low(p) < low(a) ? p : a));
    rangeStats = {
      high: [number.format(high(peak)), when(peak.t)],
      low: [number.format(low(trough)), when(trough.t)]
    };
  }

  el.innerHTML = stats(game, range, rangeStats);
}

// A group of toggles: a click on one (a `button` in `group`) turns its item
// (`item(button)`) on or off in `set`, remembered, shows it pressed or not,
// and `changed` redraws what it toggles.
function wireToggles(
  group: Element | null,
  button: string,
  item: (button: HTMLElement) => string | undefined,
  set: RememberedSet,
  changed: () => void
): void {
  group?.addEventListener("click", (event) => {
    const clicked = eventTarget(event, button);
    const toggled = clicked ? item(clicked) : undefined;
    if (!clicked || toggled === undefined) return;

    clicked.setAttribute("aria-pressed", String(set.toggle(toggled)));
    changed();
  });
}
